import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Building2,
  CheckCircle2,
  AlertCircle,
  XCircle,
  Loader2,
  Plus,
  X,
  UserRound,
  Search,
  PoundSterling,
  Clock,
  Receipt,
  CalendarDays,
  ChevronDown,
  RefreshCw,
  Trash2,
  ShoppingCart,
  UserPlus,
  Users,
} from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from '@/components/ui/collapsible';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  createCompany,
  updateCompany,
  getCompanyUsers,
  getAvailableDatabases,
  getFinOpsInstanceByName,
  updateIntegrations,
  testConnection,
  syncIntegration,
  getSyncStatus,
  removeIntegration,
  searchUsers,
  addUserToCompany,
  removeUserFromCompany,
} from '@/services/integrationsRedesignService';
import type { AvailableDatabase } from '@/types/integrations-redesign';
import {
  FINANCE_PRODUCT_LABELS,
  TIMESHEET_PRODUCT_LABELS,
  EXPENSE_PRODUCT_LABELS,
  PLANNER_PRODUCT_LABELS,
  PURCHASING_PRODUCT_LABELS,
} from '@/types/integrations-redesign';
import type {
  CompanyWithIntegrations,
  CompanyUser,
  SimpleIntegrationConfig,
  IntegrationConnectionStatus,
} from '@/types/integrations-redesign';
import { cn } from '@/lib/utils';
import SyncLogSection from '@/components/integrations/SyncLogSection';
import type { SyncLogSectionHandle } from '@/components/integrations/SyncLogSection';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface CompanySheetProps {
  company: CompanyWithIntegrations | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCompanyUpdated: (updated: CompanyWithIntegrations) => void;
  onCompanyCreated: (created: CompanyWithIntegrations) => void;
  existingCodes?: string[];
}

// ---------------------------------------------------------------------------
// Helper: formatLastSync
// ---------------------------------------------------------------------------

function formatLastSync(lastSyncAt: string | null): string {
  if (!lastSyncAt) return 'Never';
  return new Date(lastSyncAt).toLocaleString(undefined, {
    dateStyle: 'short',
    timeStyle: 'short',
  });
}

// ---------------------------------------------------------------------------
// Helper: ConnectionStatusBadge (local — avoids shared-file conflicts)
// ---------------------------------------------------------------------------

function ConnectionStatusBadge({ status }: { status: IntegrationConnectionStatus }) {
  switch (status) {
    case 'connected':
      return (
        <Badge
          variant="outline"
          className="border-success-500 text-success-700 bg-success-50 gap-1 text-xs"
        >
          <CheckCircle2 className="h-3 w-3" />
          Connected
        </Badge>
      );
    case 'error':
      return (
        <Badge variant="destructive" className="gap-1 text-xs">
          <AlertCircle className="h-3 w-3" />
          Error
        </Badge>
      );
    case 'not_connected':
      return (
        <Badge variant="secondary" className="gap-1 text-xs">
          <XCircle className="h-3 w-3" />
          Not Connected
        </Badge>
      );
    case 'not_configured':
      return (
        <Badge variant="outline" className="text-muted-foreground gap-1 text-xs">
          Not Configured
        </Badge>
      );
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// CompanyDetailsSection
// ---------------------------------------------------------------------------

interface CompanyDetailsSectionProps {
  company: CompanyWithIntegrations;
  onUpdate: (updated: CompanyWithIntegrations) => void;
}

function CompanyDetailsSection({ company, onUpdate }: CompanyDetailsSectionProps) {
  const [name, setName] = useState(company.name);
  const [code, setCode] = useState(company.code);
  const [isActive, setIsActive] = useState(company.status === 'active');
  const [isSaving, setIsSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    setName(company.name);
    setCode(company.code);
    setIsActive(company.status === 'active');
    setIsDirty(false);
  }, [company.id]); // eslint-disable-line react-hooks/exhaustive-deps

  function markDirty() {
    setIsDirty(true);
  }

  async function handleSave() {
    setIsSaving(true);
    try {
      const updated = await updateCompany(company.id, {
        name,
        code: code.toUpperCase(),
        status: isActive ? 'active' : 'inactive',
      });
      if (!updated) throw new Error('Company not found');
      onUpdate(updated);
      setIsDirty(false);
      toast.success('Company details saved');
    } catch {
      toast.error('Failed to save company details');
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="space-y-3">
        <div className="space-y-1.5">
          <Label htmlFor="company-name">Company Name</Label>
          <Input
            id="company-name"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              markDirty();
            }}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="company-code">Company Code</Label>
          <Input
            id="company-code"
            value={code}
            onChange={(e) => {
              setCode(e.target.value.toUpperCase());
              markDirty();
            }}
            placeholder="e.g. ACME"
            className="uppercase"
          />
        </div>
        <div className="flex items-center justify-between py-1">
          <div>
            <Label htmlFor="company-status" className="text-sm font-medium">
              Active
            </Label>
            <p className="text-xs text-muted-foreground mt-0.5">
              Inactive companies are hidden from the company switcher
            </p>
          </div>
          <Switch
            id="company-status"
            checked={isActive}
            onCheckedChange={(checked) => {
              setIsActive(checked);
              markDirty();
            }}
          />
        </div>
      </div>
      {isDirty && (
        <Button size="sm" onClick={handleSave} disabled={isSaving} className="w-full">
          {isSaving && <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />}
          Save Details
        </Button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// IntegrationSubSection
// ---------------------------------------------------------------------------

interface IntegrationSubSectionProps {
  companyId: number;
  type: 'finance' | 'timesheet' | 'expense' | 'planner' | 'purchasing';
  config: SimpleIntegrationConfig | undefined | null;
  onSaved: (updated: CompanyWithIntegrations) => void;
}

const FALLBACK_CONFIG: SimpleIntegrationConfig = {
  product: null,
  databaseId: null,
  status: 'not_configured',
  lastSyncAt: null,
};

function IntegrationSubSection({
  companyId,
  type,
  config: rawConfig,
  onSaved,
}: IntegrationSubSectionProps) {
  const config = rawConfig ?? FALLBACK_CONFIG;
  const [selectedProduct, setSelectedProduct] = useState(config.product ?? '');
  const [dbId, setDbId] = useState(config.databaseId ?? '');
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [availableDbs, setAvailableDbs] = useState<AvailableDatabase[]>([]);
  const [isLoadingDbs, setIsLoadingDbs] = useState(false);

  // Ref to hold the polling interval so it can be cleaned up
  const syncPollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const syncPollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clean up polling on unmount
  useEffect(() => {
    return () => {
      if (syncPollIntervalRef.current) clearInterval(syncPollIntervalRef.current);
      if (syncPollTimeoutRef.current) clearTimeout(syncPollTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    setSelectedProduct(config.product ?? '');
    setDbId(config.databaseId ?? '');
    setIsDirty(false);
  }, [config]);

  // Load databases whenever the selected product changes.
  // For planner products the backend handles the two-step pattern server-side:
  // it queries the Workspace API for instances, then calls the scheduler API
  // to resolve real company names before returning results.
  useEffect(() => {
    if (!selectedProduct) {
      setAvailableDbs([]);
      return;
    }
    let cancelled = false;
    setIsLoadingDbs(true);
    getAvailableDatabases(selectedProduct, config.databaseId ?? undefined)
      .then((dbs) => {
        if (!cancelled) {
          setAvailableDbs(dbs);
          setIsLoadingDbs(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setAvailableDbs([]);
          setIsLoadingDbs(false);
        }
      });
    return () => { cancelled = true; };
  }, [selectedProduct, config.databaseId]);

  const productOptions =
    type === 'finance'
      ? Object.entries(FINANCE_PRODUCT_LABELS)
      : type === 'timesheet'
        ? Object.entries(TIMESHEET_PRODUCT_LABELS)
        : type === 'expense'
          ? Object.entries(EXPENSE_PRODUCT_LABELS)
          : type === 'planner'
            ? Object.entries(PLANNER_PRODUCT_LABELS)
            : Object.entries(PURCHASING_PRODUCT_LABELS);

  const label =
    type === 'finance'
      ? 'Finance'
      : type === 'timesheet'
        ? 'Timesheets'
        : type === 'expense'
          ? 'Expense'
          : type === 'planner'
            ? 'Planner'
            : 'Purchasing';

  const icon =
    type === 'finance' ? (
      <PoundSterling className="h-4 w-4 text-muted-foreground flex-shrink-0" />
    ) : type === 'timesheet' ? (
      <Clock className="h-4 w-4 text-muted-foreground flex-shrink-0" />
    ) : type === 'expense' ? (
      <Receipt className="h-4 w-4 text-muted-foreground flex-shrink-0" />
    ) : type === 'planner' ? (
      <CalendarDays className="h-4 w-4 text-muted-foreground flex-shrink-0" />
    ) : (
      <ShoppingCart className="h-4 w-4 text-muted-foreground flex-shrink-0" />
    );

  async function handleSave() {
    setIsSaving(true);
    try {
      let finOpsInstanceId: string | null = null;

      // For purchasing with the FinOps product, look up the matching
      // Financials Operations instance by the selected Financials instance name.
      if (type === 'purchasing' && dbId && selectedProduct === 'access-financial-operations-purchasing') {
        const selectedDb = availableDbs.find((db) => db.id === dbId);
        if (selectedDb) {
          const finOpsInstance = await getFinOpsInstanceByName(selectedDb.label);
          if (finOpsInstance) {
            finOpsInstanceId = finOpsInstance.id;
          } else {
            toast.warning(
              `No matching Financials Operations instance found for "${selectedDb.label}"`,
            );
          }
        }
      }

      const sectionPayload: Record<string, unknown> = {
        product: selectedProduct || null,
        databaseId: dbId || null,
      };
      if (type === 'purchasing') {
        sectionPayload.finOpsInstanceId = finOpsInstanceId;
      }

      const payload = { [type]: sectionPayload };
      const updated = await updateIntegrations(companyId, payload as any);
      if (updated) {
        onSaved(updated);
        setIsDirty(false);
        toast.success(`${label} integration saved`);
      }
    } catch {
      toast.error(`Failed to save ${label.toLowerCase()} integration`);
    } finally {
      setIsSaving(false);
    }
  }

  async function handleTest() {
    setIsTesting(true);
    try {
      const result = await testConnection(companyId, type);
      if (result.success) {
        toast.success(result.message || 'Connection test successful');
        // Refetch the company so the badge updates to 'connected'
        const { getCompany } = await import('@/services/integrationsRedesignService');
        const refreshed = await getCompany(companyId);
        if (refreshed) {
          // If the backend didn't update status to 'connected' after a successful test,
          // update it on the frontend so the UI reflects the successful connection.
          const integrationKey = `${type}Integration` as keyof typeof refreshed;
          const integration = refreshed[integrationKey] as SimpleIntegrationConfig;
          if (integration && integration.status !== 'connected' && integration.product && integration.databaseId) {
            (refreshed[integrationKey] as SimpleIntegrationConfig) = {
              ...integration,
              status: 'connected',
            };
          }
          onSaved(refreshed);
        }
      } else {
        toast.error(result.message || 'Connection test failed');
      }
    } catch {
      toast.error(`Failed to test ${label.toLowerCase()} connection`);
    } finally {
      setIsTesting(false);
    }
  }

  function stopPolling() {
    if (syncPollIntervalRef.current) {
      clearInterval(syncPollIntervalRef.current);
      syncPollIntervalRef.current = null;
    }
    if (syncPollTimeoutRef.current) {
      clearTimeout(syncPollTimeoutRef.current);
      syncPollTimeoutRef.current = null;
    }
  }

  async function handleSync() {
    setIsSyncing(true);
    try {
      const result = await syncIntegration(companyId, type);

      // For finance integrations, the backend queues the sync to a background
      // worker and returns immediately — so we need to poll for completion.
      if (type === 'finance') {
        if (result.success) {
          toast.info('Sync started — you will be notified when complete');
        } else {
          toast.error('Sync failed', { description: result.message });
          setIsSyncing(false);
          return;
        }

        // Start polling every 3 seconds
        stopPolling();
        syncPollIntervalRef.current = setInterval(async () => {
          try {
            const status = await getSyncStatus(companyId);
            if (status.status === 'completed') {
              stopPolling();
              toast.success('Sync complete', { description: status.message });
              // Refresh company data
              const { getCompany } = await import('@/services/integrationsRedesignService');
              const refreshed = await getCompany(companyId);
              if (refreshed) onSaved(refreshed);
              setIsSyncing(false);
            } else if (status.status === 'failed') {
              stopPolling();
              toast.error('Sync failed', { description: status.message });
              setIsSyncing(false);
            }
            // "in-progress", "pending", "none" → keep polling
          } catch {
            stopPolling();
            toast.error('Sync failed', { description: 'Unable to check sync status' });
            setIsSyncing(false);
          }
        }, 3000);

        // Safety timeout: stop polling after 5 minutes
        syncPollTimeoutRef.current = setTimeout(() => {
          if (syncPollIntervalRef.current) {
            stopPolling();
            toast.warning('Sync is taking longer than expected — please check back later');
            setIsSyncing(false);
          }
        }, 5 * 60 * 1000);
      } else {
        // Non-finance integrations: synchronous toast pattern (existing behaviour)
        if (result.success) {
          toast.success('Sync complete', { description: result.message });
          if (result.updatedCompany) {
            onSaved(result.updatedCompany);
          }
        } else {
          toast.error('Sync failed', { description: result.message });
        }
        setIsSyncing(false);
      }
    } catch {
      toast.error(`Failed to sync ${label.toLowerCase()} integration`);
      setIsSyncing(false);
    }
  }

  async function handleRemove() {
    try {
      const updated = await removeIntegration(companyId, type);
      if (updated) {
        onSaved(updated);
        toast.success(`${label} integration removed`);
      }
    } catch {
      toast.error(`Failed to remove ${label.toLowerCase()} integration`);
    } finally {
      setShowRemoveConfirm(false);
    }
  }

  return (
    <>
      <Collapsible defaultOpen={config.status !== 'not_configured'}>
        <div className="flex items-center gap-1 rounded-md hover:bg-muted/50 transition-colors group">
          <CollapsibleTrigger className="flex-1 flex items-center gap-2 px-1 py-1.5 min-w-0">
            {icon}
            <span className="text-sm font-semibold flex-1 text-left truncate">
              {label}
            </span>
            <ConnectionStatusBadge status={config.status} />
            <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform duration-200 group-data-[state=open]:rotate-180 flex-shrink-0 ml-1" />
          </CollapsibleTrigger>
          {config.status !== 'not_configured' && (
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 flex-shrink-0 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
              onClick={(e) => {
                e.stopPropagation();
                setShowRemoveConfirm(true);
              }}
              title="Remove integration"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
        <CollapsibleContent>
          <div className="space-y-2 pl-6 pt-3">
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Product</Label>
              <Select
                value={selectedProduct}
                onValueChange={(v) => {
                  setSelectedProduct(v);
                  setDbId(''); // reset database when product changes
                  setIsDirty(true);
                }}
              >
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue placeholder="Select product..." />
                </SelectTrigger>
                <SelectContent>
                  {productOptions.map(([value, optLabel]) => (
                    <SelectItem key={value} value={value}>
                      {optLabel}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">
                {type === 'planner' || (type === 'timesheet' && selectedProduct === 'access-time') ? 'Company' : type === 'purchasing' ? 'Instance' : 'Database'}
              </Label>
              <Select
                value={dbId}
                onValueChange={(v) => {
                  setDbId(v);
                  setIsDirty(true);
                }}
                disabled={!selectedProduct || isLoadingDbs}
              >
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue
                    placeholder={
                      !selectedProduct
                        ? 'Select a product first'
                        : isLoadingDbs
                          ? (type === 'planner' || (type === 'timesheet' && selectedProduct === 'access-time')) ? 'Loading companies…' : type === 'purchasing' ? 'Loading instances…' : 'Loading databases…'
                          : availableDbs.length === 0
                            ? (type === 'planner' || (type === 'timesheet' && selectedProduct === 'access-time')) ? 'No companies available' : type === 'purchasing' ? 'No instances available' : 'No databases available'
                            : (type === 'planner' || (type === 'timesheet' && selectedProduct === 'access-time')) ? 'Select a company' : type === 'purchasing' ? 'Select instance…' : 'Select database…'
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {availableDbs.map((db) => (
                    <SelectItem key={db.id} value={db.id}>
                      <span className="flex items-center justify-between gap-3 w-full">
                        <span>{db.label}</span>
                        {db.environment && (
                          <span className="text-xs text-muted-foreground shrink-0">
                            {db.environment}
                          </span>
                        )}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {type === 'purchasing' && config.finOpsInstanceId && (
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <CheckCircle2 className="h-3 w-3 text-success-600 flex-shrink-0" />
                <span>
                  FinOps instance:{' '}
                  <span className="font-medium text-foreground">
                    {config.finOpsInstanceId}
                  </span>
                </span>
              </div>
            )}
            {config.lastSyncAt && (
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <RefreshCw className="h-3 w-3 flex-shrink-0" />
                <span>
                  Last synced:{' '}
                  <span className="font-medium text-foreground">
                    {formatLastSync(config.lastSyncAt)}
                  </span>
                </span>
              </div>
            )}
            <div className="flex flex-wrap gap-2 pt-1 pb-2">
              {config.status === 'connected' && (
                <Button
                  variant="outline"
                  size="sm"
                  className="text-xs h-7"
                  onClick={handleSync}
                  disabled={isSyncing}
                >
                  {isSyncing ? (
                    <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                  ) : (
                    <RefreshCw className="h-3 w-3 mr-1" />
                  )}
                  Sync Now
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                className="text-xs h-7"
                onClick={handleTest}
                disabled={isTesting || !selectedProduct || !dbId}
              >
                {isTesting ? (
                  <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                ) : (
                  <CheckCircle2 className="h-3 w-3 mr-1" />
                )}
                Test Connection
              </Button>
              <Button
                size="sm"
                className="text-xs h-7"
                onClick={handleSave}
                disabled={isSaving || !isDirty}
              >
                {isSaving && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
                Save
              </Button>
            </div>

          </div>
        </CollapsibleContent>
      </Collapsible>

      <AlertDialog open={showRemoveConfirm} onOpenChange={setShowRemoveConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {label} integration?</AlertDialogTitle>
            <AlertDialogDescription>
              This will disconnect and remove all configuration for{' '}
              {label.toLowerCase()}. Any active sync schedule will be cancelled.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemove}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remove Integration
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// ---------------------------------------------------------------------------
// AddUsersDialog (integrations — server-side search)
// ---------------------------------------------------------------------------

interface AddUsersDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyName: string;
  searchQuery: string;
  onSearchChange: (q: string) => void;
  availableUsers: CompanyUser[];
  isLoadingUsers: boolean;
  onUsersAdded: (ids: string[]) => Promise<void>;
}

function AddUsersDialog({
  open,
  onOpenChange,
  companyName,
  searchQuery,
  onSearchChange,
  availableUsers,
  isLoadingUsers,
  onUsersAdded,
}: AddUsersDialogProps) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isAdding, setIsAdding] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelectedIds(new Set());
  }, [open]);

  const allSelected =
    availableUsers.length > 0 && availableUsers.every((u) => selectedIds.has(u.id));
  const someSelected = availableUsers.some((u) => selectedIds.has(u.id)) && !allSelected;

  function toggleUser(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAll() {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        availableUsers.forEach((u) => next.delete(u.id));
      } else {
        availableUsers.forEach((u) => next.add(u.id));
      }
      return next;
    });
  }

  async function handleConfirm() {
    if (selectedIds.size === 0) return;
    setIsAdding(true);
    try {
      await onUsersAdded(Array.from(selectedIds));
      onOpenChange(false);
    } catch {
      toast.error('Failed to add some users');
    } finally {
      setIsAdding(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md p-0 gap-0 overflow-hidden flex flex-col max-h-[85vh]">
        <DialogHeader className="px-5 pt-5 pb-4 border-b shrink-0">
          <DialogTitle className="flex items-center gap-2 text-base">
            <UserPlus className="h-4 w-4 text-muted-foreground" />
            Add Users to {companyName}
          </DialogTitle>
          <DialogDescription className="text-xs mt-1">
            Select one or more users to grant access to this company.
          </DialogDescription>
        </DialogHeader>

        {/* Search */}
        <div className="px-4 py-3 border-b shrink-0">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
            <input
              className="flex h-8 w-full rounded-md border border-input bg-background px-3 py-1 pl-8 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              placeholder="Search by name, email, role…"
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              autoFocus
            />
          </div>
        </div>

        {/* Select all row */}
        {!isLoadingUsers && availableUsers.length > 0 && (
          <div
            className="flex items-center gap-3 px-4 py-2 bg-muted/40 border-b shrink-0 cursor-pointer hover:bg-muted/60 transition-colors"
            onClick={toggleAll}
          >
            <Checkbox
              checked={allSelected}
              data-state={someSelected ? 'indeterminate' : undefined}
              onCheckedChange={toggleAll}
              className="pointer-events-none"
            />
            <span className="text-xs font-medium text-muted-foreground">
              {allSelected
                ? 'Deselect all'
                : someSelected
                  ? `${selectedIds.size} selected — select all ${availableUsers.length}`
                  : `Select all ${availableUsers.length} user${availableUsers.length !== 1 ? 's' : ''}`}
            </span>
          </div>
        )}

        {/* User list */}
        <div className="flex-1 overflow-y-auto min-h-0">
          {isLoadingUsers ? (
            <div className="space-y-2 p-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="flex items-center gap-3 py-1">
                  <Skeleton className="h-4 w-4 rounded" />
                  <Skeleton className="h-7 w-7 rounded-full" />
                  <div className="space-y-1 flex-1">
                    <Skeleton className="h-3 w-32" />
                    <Skeleton className="h-3 w-20" />
                  </div>
                </div>
              ))}
            </div>
          ) : availableUsers.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-center gap-2 px-4">
              {searchQuery ? (
                <>
                  <Search className="h-7 w-7 text-muted-foreground/40" />
                  <p className="text-sm text-muted-foreground">No users match "{searchQuery}"</p>
                </>
              ) : (
                <>
                  <Users className="h-7 w-7 text-muted-foreground/40" />
                  <p className="text-sm text-muted-foreground">All users already have access</p>
                </>
              )}
            </div>
          ) : (
            <div className="divide-y">
              {availableUsers.map((user) => {
                const isSelected = selectedIds.has(user.id);
                return (
                  <div
                    key={user.id}
                    className={cn(
                      'flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-colors',
                      isSelected ? 'bg-primary/5' : 'hover:bg-muted/50',
                    )}
                    onClick={() => toggleUser(user.id)}
                  >
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => toggleUser(user.id)}
                      className="pointer-events-none shrink-0"
                    />
                    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                      {user.name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium leading-tight truncate">{user.name}</div>
                      <div className="text-xs text-muted-foreground truncate">{user.role}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <DialogFooter className="px-4 py-3 border-t shrink-0 gap-2 sm:gap-2">
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} disabled={isAdding}>
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleConfirm}
            disabled={selectedIds.size === 0 || isAdding}
            className="gap-1.5"
          >
            {isAdding ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <UserPlus className="h-3.5 w-3.5" />
            )}
            {selectedIds.size > 0
              ? `Add ${selectedIds.size} User${selectedIds.size !== 1 ? 's' : ''}`
              : 'Add Users'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// BulkRemoveBar (integrations)
// ---------------------------------------------------------------------------

function BulkRemoveBar({
  selectedCount,
  onRemove,
  onClear,
  isRemoving,
}: {
  selectedCount: number;
  onRemove: () => void;
  onClear: () => void;
  isRemoving: boolean;
}) {
  if (selectedCount === 0) return null;
  return (
    <div className="sticky bottom-0 left-0 right-0 z-10 pointer-events-none">
      <div className="flex justify-center pb-2">
        <div className="pointer-events-auto flex items-center gap-2 px-4 py-2 rounded-full bg-primary text-primary-foreground shadow-2xl text-sm whitespace-nowrap animate-in slide-in-from-bottom-4 duration-300">
          <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-white/25 text-xs font-bold px-1">
            {selectedCount}
          </span>
          <span className="font-medium pr-1">{selectedCount === 1 ? 'user' : 'users'} selected</span>
          <div className="h-4 w-px bg-white/30" />
          <button
            onClick={onRemove}
            disabled={isRemoving}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/15 hover:bg-white/25 transition-colors font-medium disabled:opacity-60"
          >
            {isRemoving ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Trash2 className="h-3.5 w-3.5" />
            )}
            Remove Access
          </button>
          <div className="h-4 w-px bg-white/30" />
          <button
            onClick={onClear}
            className="flex items-center gap-1 text-white/80 hover:text-white transition-colors px-1"
            aria-label="Clear selection"
          >
            <X className="h-3.5 w-3.5" />
            <span className="text-xs">Clear</span>
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// UsersWithAccessSection
// ---------------------------------------------------------------------------

interface UsersWithAccessSectionProps {
  company: CompanyWithIntegrations;
  onUserCountChange: (count: number) => void;
}

function UsersWithAccessSection({
  company,
  onUserCountChange,
}: UsersWithAccessSectionProps) {
  const [users, setUsers] = useState<CompanyUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filterQuery, setFilterQuery] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [allAvailableUsers, setAllAvailableUsers] = useState<CompanyUser[]>([]);
  const [isLoadingAvailable, setIsLoadingAvailable] = useState(false);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [removingUser, setRemovingUser] = useState<CompanyUser | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const [bulkSelectedIds, setBulkSelectedIds] = useState<Set<string>>(new Set());
  const [showBulkRemoveConfirm, setShowBulkRemoveConfirm] = useState(false);
  const [isBulkRemoving, setIsBulkRemoving] = useState(false);

  const loadUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await getCompanyUsers(company.id);
      setUsers(data);
      onUserCountChange(data.length);
    } catch {
      setUsers([]);
      onUserCountChange(0);
    } finally {
      setIsLoading(false);
    }
  }, [company.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  // Server-side search: fetch users from the API whenever the popover
  // is open and the search query changes (debounced).
  useEffect(() => {
    if (!isAddOpen) {
      setAllAvailableUsers([]);
      setSearchQuery('');
      return;
    }
    let cancelled = false;
    setIsLoadingAvailable(true);
    const debounce = setTimeout(() => {
      searchUsers(searchQuery, company.id)
        .then((results) => {
          if (!cancelled) setAllAvailableUsers(results);
        })
        .catch(() => {
          if (!cancelled) setAllAvailableUsers([]);
        })
        .finally(() => {
          if (!cancelled) setIsLoadingAvailable(false);
        });
    }, searchQuery ? 300 : 0);
    return () => { cancelled = true; clearTimeout(debounce); };
  }, [isAddOpen, company.id, searchQuery]);

  const filteredUsers = filterQuery.trim()
    ? users.filter(
        (u) =>
          u.name.toLowerCase().includes(filterQuery.toLowerCase()) ||
          u.role.toLowerCase().includes(filterQuery.toLowerCase()),
      )
    : users;

  const allBulkSelected =
    filteredUsers.length > 0 && filteredUsers.every((u) => bulkSelectedIds.has(u.id));
  const someBulkSelected =
    filteredUsers.some((u) => bulkSelectedIds.has(u.id)) && !allBulkSelected;

  function toggleBulkUser(id: string) {
    setBulkSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAllBulk() {
    setBulkSelectedIds((prev) => {
      const next = new Set(prev);
      if (allBulkSelected) {
        filteredUsers.forEach((u) => next.delete(u.id));
      } else {
        filteredUsers.forEach((u) => next.add(u.id));
      }
      return next;
    });
  }

  async function handleUsersAdded(ids: string[]) {
    await Promise.all(ids.map((id) => addUserToCompany(company.id, id)));
    await loadUsers();
    setAllAvailableUsers((prev) => prev.filter((u) => !ids.includes(u.id)));
    setSearchQuery('');
    toast.success(
      `${ids.length} user${ids.length !== 1 ? 's' : ''} added to ${company.name}`,
    );
  }

  async function confirmBulkRemove() {
    setIsBulkRemoving(true);
    const ids = Array.from(bulkSelectedIds);
    try {
      await Promise.all(ids.map((id) => removeUserFromCompany(company.id, id)));
      await loadUsers();
      setBulkSelectedIds(new Set());
      toast.success(
        `${ids.length} user${ids.length !== 1 ? 's' : ''} removed from ${company.name}`,
      );
    } catch {
      toast.error('Failed to remove some users');
    } finally {
      setIsBulkRemoving(false);
      setShowBulkRemoveConfirm(false);
    }
  }

  return (
    <div className="space-y-3">
      {/* Add Users button */}
      <div className="flex items-center justify-end">
        <Button
          variant="outline"
          size="sm"
          className="h-8 text-xs gap-1 shrink-0"
          onClick={() => setIsAddOpen(true)}
        >
          <UserPlus className="h-3.5 w-3.5" />
          Add Users
        </Button>
      </div>

      {/* Count line */}
      {!isLoading && (
        <p className="text-xs text-muted-foreground">
          {filterQuery.trim()
            ? `${filteredUsers.length} of ${users.length} user${users.length !== 1 ? 's' : ''} match`
            : `${users.length} user${users.length !== 1 ? 's' : ''} with access`}
        </p>
      )}

      {/* Loading skeletons */}
      {isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-center gap-2 py-1">
              <Skeleton className="h-8 w-8 rounded-full" />
              <div className="space-y-1 flex-1">
                <Skeleton className="h-3 w-28" />
                <Skeleton className="h-3 w-16" />
              </div>
            </div>
          ))}
        </div>
      ) : users.length === 0 ? (
        /* Empty state */
        <div className="flex flex-col items-center justify-center py-10 text-center gap-2">
          <UserRound className="h-8 w-8 text-muted-foreground/40" />
          <p className="text-sm text-muted-foreground">No users assigned yet.</p>
          <p className="text-xs text-muted-foreground">
            Use "Add User" to grant access to this company.
          </p>
        </div>
      ) : (
        /* User list with bulk-select */
        <div className="rounded-lg border border-border overflow-hidden">
          {/* Select all header */}
          <div
            className="flex items-center gap-2.5 px-3 py-2 bg-muted/40 border-b cursor-pointer hover:bg-muted/60 transition-colors"
            onClick={toggleAllBulk}
          >
            <Checkbox
              checked={allBulkSelected}
              data-state={someBulkSelected ? 'indeterminate' : undefined}
              onCheckedChange={toggleAllBulk}
              className="pointer-events-none shrink-0"
            />
            <span className="text-xs font-medium text-muted-foreground">
              {allBulkSelected
                ? 'Deselect all'
                : someBulkSelected
                  ? `${bulkSelectedIds.size} selected — select all ${filteredUsers.length}`
                  : `Select all ${filteredUsers.length} user${filteredUsers.length !== 1 ? 's' : ''}`}
            </span>
          </div>
          <div className="divide-y">
            {filteredUsers.map((user) => {
              const isSelected = bulkSelectedIds.has(user.id);
              return (
                <div
                  key={user.id}
                  className={cn(
                    'flex items-center gap-2.5 px-3 py-2 cursor-pointer transition-colors group',
                    isSelected ? 'bg-primary/5' : 'hover:bg-muted/50',
                  )}
                  onClick={() => toggleBulkUser(user.id)}
                >
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={() => toggleBulkUser(user.id)}
                    className="pointer-events-none shrink-0"
                  />
                  <div className="h-7 w-7 rounded-full bg-muted flex items-center justify-center flex-shrink-0 text-xs font-medium">
                    {user.name.split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium leading-tight truncate">{user.name}</div>
                    <div className="text-xs text-muted-foreground truncate">{user.role}</div>
                  </div>
                  <button
                    className={cn(
                      'opacity-0 group-hover:opacity-100 transition-opacity',
                      'h-7 w-7 flex items-center justify-center rounded-md shrink-0',
                      'text-muted-foreground hover:text-destructive hover:bg-destructive/10',
                    )}
                    onClick={(e) => {
                      e.stopPropagation();
                      setRemovingUser(user);
                    }}
                    title={`Remove ${user.name}`}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Bulk remove bar */}
      <BulkRemoveBar
        selectedCount={bulkSelectedIds.size}
        onRemove={() => setShowBulkRemoveConfirm(true)}
        onClear={() => setBulkSelectedIds(new Set())}
        isRemoving={isBulkRemoving}
      />

      {/* Footer note */}
      <p className="text-xs text-muted-foreground pt-2 border-t">
        To manage a user's full company access, go to{' '}
        <span className="font-medium">Team Members</span>.
      </p>

      {/* Remove user confirm */}
      <AlertDialog
        open={!!removingUser}
        onOpenChange={(o) => {
          if (!o) setRemovingUser(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove access?</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{removingUser?.name}</strong> will lose access to{' '}
              <strong>{company.name}</strong>. They will no longer see this
              company in their switcher.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={isRemoving}
              onClick={async () => {
                setIsRemoving(true);
                try {
                  await removeUserFromCompany(company.id, removingUser!.id);
                  await loadUsers();
                  toast.success(`${removingUser!.name} removed from ${company.name}`);
                } catch {
                  toast.error(`Failed to remove ${removingUser?.name}`);
                } finally {
                  setIsRemoving(false);
                  setRemovingUser(null);
                }
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isRemoving ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : null}
              Remove Access

            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk remove confirm */}
      <AlertDialog open={showBulkRemoveConfirm} onOpenChange={setShowBulkRemoveConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Remove {bulkSelectedIds.size} user{bulkSelectedIds.size !== 1 ? 's' : ''}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              These users will lose access to <strong>{company.name}</strong> and will no longer
              see it in their company switcher.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isBulkRemoving}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={isBulkRemoving}
              onClick={confirmBulkRemove}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isBulkRemoving ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : null}
              Remove Access
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add users dialog */}
      <AddUsersDialog
        open={isAddOpen}
        onOpenChange={setIsAddOpen}
        companyName={company.name}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        availableUsers={allAvailableUsers}
        isLoadingUsers={isLoadingAvailable}
        onUsersAdded={handleUsersAdded}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// CreateCompanyForm
// ---------------------------------------------------------------------------

interface CreateCompanyFormProps {
  existingCodes: string[];
  onCreated: (company: CompanyWithIntegrations) => void;
  onCancel: () => void;
}

function CreateCompanyForm({
  existingCodes,
  onCreated,
  onCancel,
}: CreateCompanyFormProps) {
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [status, setStatus] = useState<'active' | 'inactive'>('active');
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<{ name?: string; code?: string }>({});

  function validate() {
    const e: { name?: string; code?: string } = {};
    if (!name.trim() || name.trim().length < 2)
      e.name = 'Company name must be at least 2 characters';
    if (!code.trim() || code.trim().length < 2)
      e.code = 'Company code must be at least 2 characters';
    else if (code.trim().length > 20)
      e.code = 'Company code must be at most 20 characters';
    else if (!/^[A-Z0-9-]+$/.test(code.trim()))
      e.code = 'Uppercase letters, numbers, and hyphens only';
    else if (
      existingCodes.map((c) => c.toUpperCase()).includes(code.trim().toUpperCase())
    )
      e.code = 'This company code is already in use';
    return e;
  }

  async function handleSave() {
    const e = validate();
    if (Object.keys(e).length) {
      setErrors(e);
      return;
    }
    setIsSaving(true);
    try {
      const created = await createCompany({
        name: name.trim(),
        code: code.trim().toUpperCase(),
        status,
      });
      toast.success(`${created.name} created`);
      onCreated(created);
    } catch {
      toast.error('Failed to create company');
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div className="px-6 py-5 space-y-5">
      <div className="space-y-1.5">
        <Label htmlFor="new-company-name">
          Company Name <span className="text-destructive">*</span>
        </Label>
        <Input
          id="new-company-name"
          value={name}
          placeholder="e.g., UK Operations"
          onChange={(e) => {
            setName(e.target.value);
            setErrors((prev) => ({ ...prev, name: undefined }));
          }}
          disabled={isSaving}
        />
        {errors.name && (
          <p className="text-xs text-destructive">{errors.name}</p>
        )}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="new-company-code">
          Company Code <span className="text-destructive">*</span>
        </Label>
        <Input
          id="new-company-code"
          value={code}
          placeholder="e.g., UK-OPS"
          className="uppercase"
          onChange={(e) => {
            setCode(e.target.value.toUpperCase());
            setErrors((prev) => ({ ...prev, code: undefined }));
          }}
          disabled={isSaving}
        />
        <p className="text-xs text-muted-foreground">
          Unique identifier — uppercase letters, numbers and hyphens only
        </p>
        {errors.code && (
          <p className="text-xs text-destructive">{errors.code}</p>
        )}
      </div>

      <div className="flex items-center justify-between py-1">
        <div>
          <Label htmlFor="new-company-status" className="text-sm font-medium">
            Active
          </Label>
          <p className="text-xs text-muted-foreground mt-0.5">
            Inactive companies are hidden from the company switcher
          </p>
        </div>
        <Switch
          id="new-company-status"
          checked={status === 'active'}
          onCheckedChange={(checked) => setStatus(checked ? 'active' : 'inactive')}
          disabled={isSaving}
        />
      </div>

      <Separator />

      <div className="flex gap-2 pt-1">
        <Button
          variant="outline"
          className="flex-1"
          onClick={onCancel}
          disabled={isSaving}
        >
          Cancel
        </Button>
        <Button className="flex-1" onClick={handleSave} disabled={isSaving}>
          {isSaving && <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />}
          Create Company
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// CompanySheet — main export
// ---------------------------------------------------------------------------

export function CompanySheet({
  company,
  open,
  onOpenChange,
  onCompanyUpdated,
  onCompanyCreated,
  existingCodes = [],
}: CompanySheetProps) {
  const syncLogRef = useRef<SyncLogSectionHandle>(null);
  const [activeTab, setActiveTab] = useState('details');

  function handleUserCountChange(count: number) {
    if (!company) return;
    onCompanyUpdated({ ...company, userCount: count });
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-xl p-0 flex flex-col overflow-hidden"
      >
        {/* Header */}
        <SheetHeader className="px-6 pt-6 pb-4 border-b shrink-0">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-md bg-muted">
              <Building2 className="h-5 w-5 text-muted-foreground" />
            </div>
            <div className="min-w-0">
              <SheetTitle className="text-base leading-tight truncate">
                {!company ? 'Add New Company' : company.name}
              </SheetTitle>
              <SheetDescription className="text-xs">
                {!company
                  ? 'Fill in the details below to create a new company'
                  : company.code}
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        {/* Body */}
        {!company ? (
          <CreateCompanyForm
            existingCodes={existingCodes}
            onCreated={(created) => {
              onCompanyCreated(created);
              onOpenChange(false);
            }}
            onCancel={() => onOpenChange(false)}
          />
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col flex-1 min-h-0">
            {/* Tab bar */}
            <div className="px-6 pt-3 pb-0 border-b shrink-0">
              <TabsList className="h-9 w-full rounded-none bg-transparent p-0 gap-0 justify-start">
                <TabsTrigger
                  value="details"
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 h-9 text-sm"
                >
                  Details & Integrations
                </TabsTrigger>
                <TabsTrigger
                  value="users"
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 h-9 text-sm"
                >
                  Users with Access
                  {company.userCount > 0 && (
                    <span className="ml-1.5 inline-flex items-center justify-center rounded-full bg-muted text-muted-foreground text-[10px] font-medium h-4 min-w-4 px-1">
                      {company.userCount}
                    </span>
                  )}
                </TabsTrigger>
                <TabsTrigger
                  value="sync-log"
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 h-9 text-sm"
                >
                  Sync Log
                </TabsTrigger>
              </TabsList>
            </div>

            {/* Tab 1: Details & Integrations */}
            <TabsContent value="details" className="flex-1 overflow-y-auto mt-0">
              <div className="px-6 py-5 space-y-6">
                <section>
                  <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                    Company Details
                  </h3>
                  <CompanyDetailsSection
                    company={company}
                    onUpdate={onCompanyUpdated}
                  />
                </section>
                <Separator />
                <section className="pb-6">
                  <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                    Integrations
                  </h3>
                  <div className="space-y-1">
                    <IntegrationSubSection
                      companyId={company.id}
                      type="finance"
                      config={company.financeIntegration}
                      onSaved={onCompanyUpdated}
                    />
                    <IntegrationSubSection
                      companyId={company.id}
                      type="timesheet"
                      config={company.timesheetIntegration}
                      onSaved={onCompanyUpdated}
                    />
                    <IntegrationSubSection
                      companyId={company.id}
                      type="expense"
                      config={company.expenseIntegration}
                      onSaved={onCompanyUpdated}
                    />
                    <IntegrationSubSection
                      companyId={company.id}
                      type="planner"
                      config={company.plannerIntegration}
                      onSaved={onCompanyUpdated}
                    />
                    <IntegrationSubSection
                      companyId={company.id}
                      type="purchasing"
                      config={company.purchasingIntegration}
                      onSaved={onCompanyUpdated}
                    />
                  </div>
                </section>
              </div>
            </TabsContent>

            {/* Tab 2: Users with Access */}
            <TabsContent value="users" className="flex-1 overflow-y-auto mt-0">
              <div className="px-6 py-5 pb-8">
                <UsersWithAccessSection
                  company={company}
                  onUserCountChange={handleUserCountChange}
                />
              </div>
            </TabsContent>
            {/* Tab 3: Sync Log */}
            <TabsContent value="sync-log" className="flex-1 overflow-y-auto mt-0">
              <div className="px-6 py-5 pb-8">
                <SyncLogSection ref={syncLogRef} companyId={company.id} />
              </div>
            </TabsContent>
          </Tabs>
        )}
      </SheetContent>
    </Sheet>
  );
}

export default CompanySheet;
